/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { If, Then, Else } from "react-if";
import { BrickLoader, Popover, theme } from "@nordcloud/gnui";
import { LoaderWrap } from "~/components";
import { useGetDataByIds } from "~/components/ExpandableTag/hooks";
import { QueryStateFilterValue } from "~/hooks";
import { isNotNil, isNotEmpty } from "~/tools";
import { CARBON_FOOTPRINT_SUGGESTION } from "../types";
import { FilterTag } from "./FilterTag";
import { FilterValues } from "./FilterValues";
import { mapValuesToArray } from "./helpers";

type Props = {
  name: string;
  value: QueryStateFilterValue;
  excludedFilters: string[];
  onRemoveKey: () => void;
  onRemoveValue: (key: string, index: number) => void;
};

export function ActiveFilterItem({
  name,
  value,
  excludedFilters,
  onRemoveKey,
  onRemoveValue,
}: Props) {
  const valueArray = mapValuesToArray(value, name);

  const { loading, error, data } = useGetDataByIds({
    name,
    value: valueArray,
  });

  const onRemoveValueHandler = (inputName: string, index: number) => {
    onRemoveValue(inputName, index);
  };

  const mappedData = mapFiltersNames(valueArray, data);

  const isFilterExcluded = excludedFilters.includes(name);

  return (
    <LoaderWrap
      loading={loading}
      Component={BrickLoader}
      viewBox="0 0 7 1"
      css={{ maxWidth: "10rem", marginRight: theme.spacing.spacing02 }}
    >
      <If condition={isNotEmpty(mappedData) || isNotEmpty(valueArray)}>
        <Then>
          <Popover
            trigger={
              <FilterTag
                name={name}
                value={value}
                isFilterExcluded={isFilterExcluded}
                valueArray={valueArray}
                onRemoveKey={onRemoveKey}
              />
            }
          >
            <FilterValues
              name={name}
              mappedFilterOptions={mappedData}
              valueArray={valueArray}
              isFilterExcluded={isFilterExcluded}
              error={error}
              onRemoveValueHandler={onRemoveValueHandler}
            />
          </Popover>
        </Then>
        <Else>
          <FilterTag
            name={name}
            value={value}
            isFilterExcluded={isFilterExcluded}
            valueArray={valueArray}
            onRemoveKey={onRemoveKey}
          />
        </Else>
      </If>
    </LoaderWrap>
  );
}

function mapFiltersNames(
  valueArray: string[],
  data: {
    nid: string;
    name: string;
  }[]
) {
  const mappedArray = valueArray
    .map((element) => data.find((d) => d.nid === element))
    .filter(isNotNil);

  if (valueArray.includes(CARBON_FOOTPRINT_SUGGESTION.nid)) {
    return [...mappedArray, CARBON_FOOTPRINT_SUGGESTION];
  }

  return mappedArray;
}
